import './Login.css';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Col, Input, Row, Typography } from 'antd';
import LoginService from '../../services/LoginService';
import { configureApiClient } from '../../services/apiClient';
import {
   ALREADY_LOGGED_IN_MESSAGE,
   AUTHENTICATION_FAILED_TITLE,
   EMPTY_CREDENTIALS_MESSAGE,
   INVALID_CREDENTIALS_MESSAGE,
   getLoginError,
} from './loginError';
import Enums from '../../misc/Enums';
import {
   getLocalLoginAssets,
   TENANT_LOGIN_ASSETS,
   markTenantAssetMissing,
   resolveLoginAssetsSync,
} from '../../misc/TenantAssets';
import jwtDecode from 'jwt-decode';
import {
   EyeInvisibleOutlined,
   EyeTwoTone,
   LockOutlined,
   UserOutlined,
} from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;

export default function Login() {
   const navigate = useNavigate();
   const [userName, setUsername] = useState('');
   const [password, setPassword] = useState('');
   const [invalidLogin, setInvalidLogin] = useState(false);
   const [userType] = useState('1');
   const [signInLoading, setSignInLoading] = useState(false);
   const [signInText, setSignInText] = useState('Sign In');
   // Branding is resolved synchronously from the tenant subdomain, so the very
   // first paint already uses the tenant assets instead of the bundled defaults
   // (which previously flashed before the async lookup completed).
   const [schoolPhotoSrc, setSchoolPhotoSrc] = useState(() => resolveLoginAssetsSync().photo);
   const [invalidLoginMsg, setInvalidLoginMsg] = useState(null);
   // Title and description are set together. A single "Authentication failed" title used to cover a
   // wrong password, an unreachable database (500) and a request that never left the browser, which
   // told the user their credentials were wrong when the server was at fault (pages/LoginPage/loginError.js).
   const [invalidLoginTitle, setInvalidLoginTitle] = useState(AUTHENTICATION_FAILED_TITLE);

   const version = `${process.env.REACT_APP_VERSION}`;
   const [logoSrc, setLogoSrc] = useState(() => resolveLoginAssetsSync().logo);
   const localAssets = getLocalLoginAssets();
   const schoolPhotoAlt = 'School learning environment';

   // Fall back to the bundled image when the tenant has not uploaded its own,
   // and record the miss so later mounts skip the doomed request entirely.
   const handleLogoSrcError = () => {
      markTenantAssetMissing(TENANT_LOGIN_ASSETS.logo);
      setLogoSrc((prev) => (prev === localAssets.logo ? prev : localAssets.logo));
   };

   const handleSchoolPhotoSrcError = () => {
      markTenantAssetMissing(TENANT_LOGIN_ASSETS.photo);
      setSchoolPhotoSrc((prev) =>
         prev === localAssets.photo ? prev : localAssets.photo
      );
   };

   const redirectLogin = () => {
      setSignInLoading(true);
      setInvalidLogin(false);
      setSignInText('Signing in...');

      const request = { UserName: userName, Password: password, UserType: userType };

      if (userName !== '' && password !== '') {
         LoginService.loginService(request)
            .then((response) => {
               if (response.data !== '' && response.data !== 'already logged in') {
                  setInvalidLogin(false);
                  localStorage.setItem('lms-jwt', response.data);
                  // Re-installs both headers (token + tenant) through the single owner in
                  // services/apiClient.js, so a login never depends on axios.defaults still
                  // holding the pre-login values (R-5).
                  configureApiClient();

                  const decodedString = jwtDecode(response.data);
                  const otherData = JSON.parse(decodedString[Enums.UserInfo.otherdata]);

                  if (
                     decodedString.role === 'Registrar' ||
                     decodedString.role === 'Guidance' ||
                     decodedString.role === 'Cashier'
                  ) {
                     navigate('/applicantlist', { replace: true });
                  } else if (decodedString.role === 'Student' && otherData.Status === '0') {
                     navigate('/studentassessment', { replace: true });
                  } else if (decodedString.role === 'Parent' && otherData.Status === '0') {
                     navigate('/studentfees', { replace: true });
                  } else {
                     navigate('/home', { replace: true });
                  }
               } else {
                  setInvalidLoginTitle(AUTHENTICATION_FAILED_TITLE);

                  if (response.data === 'already logged in') {
                     setInvalidLoginMsg(ALREADY_LOGGED_IN_MESSAGE);
                  } else {
                     setInvalidLoginMsg(INVALID_CREDENTIALS_MESSAGE);
                  }

                  setSignInText('Sign In');
                  setSignInLoading(false);
                  setInvalidLogin(true);
               }
            })
            .catch((error) => {
               // The API answers "wrong password" with 200 + an empty body, so a rejection here is
               // never a credential problem: it is a 5xx (the API could not reach MySQL), a network
               // failure, or a rejected certificate. Report which one instead of blaming the user.
               const { title, description } = getLoginError(error);

               setSignInText('Sign In');
               setSignInLoading(false);
               setInvalidLoginTitle(title);
               setInvalidLoginMsg(description);
               setInvalidLogin(true);
            });
      } else {
         setSignInText('Sign In');
         setSignInLoading(false);
         setInvalidLoginTitle(AUTHENTICATION_FAILED_TITLE);
         setInvalidLoginMsg(EMPTY_CREDENTIALS_MESSAGE);
         setInvalidLogin(true);
      }
   };

   const alertOnClose = () => {
      setInvalidLogin(false);
   };

   const forgotPassword = () => {
      setInvalidLogin(false);
      window.location.href = '/forgot-password';
   };

   // The legacy pre-login Privacy Policy `Modal.confirm` gate (D7) was removed on 2026-10-30 at the
   // owner's request: the Sign In button and both Enter keys call redirectLogin() directly, and the
   // inline "By clicking Sign In…" notice below the button is the only privacy text, as it is on
   // CampusCloud. `requiresPrivacyAcknowledgement()` was removed from misc/TvetTenant.js with it.

   return (
      <div className="login-page">
         <div className="login-shell">
            <section className="login-hero">
               <div className="login-hero__backdrop" />
               <div className="login-hero__content">
                  <div className="login-hero__brand">
                     <div className="login-hero__brand-mark">
                        <img
                           src={logoSrc}
                           alt="School logo"
                           onError={handleLogoSrcError}
                        />
                     </div>
                     <div>
                        <Text className="login-hero__eyebrow">Learning Management Portal</Text>
                        <Title level={1} className="login-hero__title">
                           Welcome to your academic workspace
                        </Title>
                        <Paragraph className="login-hero__text">
                           A modern, secure platform for students, parents, and staff to access
                           school services in one place.
                        </Paragraph>
                     </div>
                  </div>

                  <div className="login-hero__photo">
                     <img
                        src={schoolPhotoSrc}
                        alt={schoolPhotoAlt}
                        onError={handleSchoolPhotoSrcError}
                     />
                  </div>
               </div>
            </section>

            <section className="login-panel">
               <div className="login-panel__card">
                  <div className="login-panel__header">
                     <img
                        className="login-panel__logo"
                        src={logoSrc}
                        alt="School logo"
                        onError={handleLogoSrcError}
                     />
                     {/* <Text className="login-panel__kicker">Secure Access</Text> */}
                     <Title level={2} className="login-panel__title">
                        Sign in to continue
                     </Title>
                     <Paragraph className="login-panel__subtitle">
                        Enter your credentials to access your dashboard and school services.
                     </Paragraph>
                  </div>

                  {invalidLogin ? (
                     <Alert
                        className="login-panel__alert"
                        message={invalidLoginTitle}
                        description={invalidLoginMsg}
                        type="error"
                        closable
                        onClose={alertOnClose}
                     />
                  ) : null}

                  <div className="login-form">
                     <Row gutter={[0, 16]}>
                        <Col span={24}>
                           <label className="login-form__label">Username</label>
                           <Input
                              disabled={signInLoading}
                              allowClear
                              className="login-form__input"
                              size="large"
                              type="text"
                              autoComplete="off"
                              value={userName}
                              onChange={(e) => setUsername(e.target.value)}
                              prefix={<UserOutlined />}
                              onKeyPress={(e) => {
                                 if (e.key === 'Enter') {
                                    redirectLogin();
                                 }
                              }}
                           />
                        </Col>
                        <Col span={24}>
                           <label className="login-form__label">Password</label>
                           <Input.Password
                              disabled={signInLoading}
                              allowClear
                              className="login-form__input"
                              size="large"
                              autoComplete="off"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              prefix={<LockOutlined />}
                              iconRender={(visible) =>
                                 visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                              }
                              onPressEnter={() => redirectLogin()}
                           />
                        </Col>
                        <Col span={24}>
                           <Button
                              className="login-form__button"
                              size="large"
                              block
                              loading={signInLoading}
                              type="primary"
                              onClick={() => redirectLogin()}
                           >
                              {signInText}
                           </Button>
                        </Col>
                     </Row>

                     <div className="login-form__meta">
                        <Text className="login-form__privacy">
                           By clicking &quot;Sign In&quot;, you agree to our{' '}
                           <a
                              href="#"
                              onClick={(e) => {
                                 e.preventDefault();
                                 window.open('/privacy-policy', '_blank').focus();
                              }}
                           >
                              Privacy Statement
                           </a>
                           .
                        </Text>
                        <p className="forgot-password">
                           Forgot{' '}
                           <a
                              href="#"
                              onClick={(e) => {
                                 e.preventDefault();
                                 forgotPassword();
                              }}
                           >
                              password
                           </a>
                           ?
                        </p>
                     </div>
                  </div>

                  <div className="login-panel__footer">
                     Brainee Powered by Campus Cloud IT Consultancy
                     <span>Version {version}</span>
                  </div>
               </div>
            </section>
         </div>
      </div>
   );
}
